import { useMemo, useState } from "react";
import { LuCoins, LuDollarSign, LuMessagesSquare } from "react-icons/lu";
import {
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  SegmentGroup,
  Separator,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { BarSegment, Chart, useChart } from "@chakra-ui/charts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
  Tooltip,
  usePlotArea,
  useXAxisScale,
  XAxis,
  YAxis,
} from "recharts";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { useQuery } from "@/lib/convex";
import { PageHeader } from "../components/PageHeader";
import { StatCard } from "../components/StatCard";
import { useEffectivePersonId } from "../impersonation";

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type UsageCell = {
  bucket: string;
  model: string;
  origin: string;
  requests: number;
  inputTokens: number;
  inputCachedTokens: number;
  outputTokens: number;
  outputImageTokens: number;
  costUsd: number;
  costInputUsd: number;
  costInputCachedUsd: number;
  costOutputUsd: number;
  costOutputImageUsd: number;
};

type Metric = "tokens" | "cost" | "requests";
type Split = "total" | "origin" | "model" | "type";

const TYPE_CATS = ["Input", "Cached", "Output", "Image"] as const;
type TypeCat = (typeof TYPE_CATS)[number];

const TYPE_COLORS: Record<TypeCat, string> = {
  Input: "blue.solid",
  Cached: "purple.solid",
  Output: "teal.solid",
  Image: "orange.solid",
};

const PALETTE = [
  "blue.solid",
  "teal.solid",
  "purple.solid",
  "orange.solid",
  "pink.solid",
  "cyan.solid",
  "red.solid",
  "green.solid",
  "yellow.solid",
  "gray.solid",
];

// Ordered to mirror the KPI cards above (cost, tokens, requests).
const METRIC_OPTIONS: { label: string; value: Metric }[] = [
  { label: "Cost", value: "cost" },
  { label: "Tokens", value: "tokens" },
  { label: "Requests", value: "requests" },
];

// Labels read together with the "Group {unit} by" group label, so they carry no
// "By " prefix; "total" reads as grouping by nothing.
const SPLIT_OPTIONS: { label: string; value: Split }[] = [
  { label: "(none)", value: "total" },
  { label: "Origin", value: "origin" },
  { label: "Model", value: "model" },
  { label: "Type", value: "type" },
];

const HOUR_BUCKETS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));
const CHART_HEIGHT = "240px";
const GRID_STROKE = "border.muted";
const HOVER_CURSOR = "rgba(128, 128, 128, 0.14)";
const SELECT_BG = "rgba(128, 128, 128, 0.30)";

// Shared uppercase "eyebrow" label style, reused by the StatCards and the
// details-panel title so they read as one system.
const SECTION_LABEL_PROPS = {
  fontSize: "xs",
  fontWeight: "semibold",
  textTransform: "uppercase",
  letterSpacing: "0.24em",
  color: "fg.muted",
} as const;

// ---------------------------------------------------------------------------
// Date helpers (Europe/Vienna, mirrors backend bucketing)
// ---------------------------------------------------------------------------

function viennaTodayStr(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Vienna",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function addDays(day: string, delta: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + delta);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function enumerateDays(from: string, to: string): string[] {
  const days: string[] = [];
  let cursor = from;
  for (let i = 0; i < 400 && cursor <= to; i++) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
}

// ---------------------------------------------------------------------------
// Formatting (USD stored as-is; cost always 4 decimals, tiny values clamped)
// ---------------------------------------------------------------------------

const tokensFull = new Intl.NumberFormat("en-US");
const tokensCompact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

function formatCostValue(value: number): string {
  if (value > 0 && value < 0.0001) return "<$0.0001";
  return `$${value.toFixed(4)}`;
}

function formatCostAxis(value: number): string {
  return `$${value.toFixed(4)}`;
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

function tokensTotal(cell: UsageCell): number {
  return cell.inputTokens + cell.inputCachedTokens + cell.outputTokens + cell.outputImageTokens;
}

function cellMetric(cell: UsageCell, metric: Metric): number {
  if (metric === "tokens") return tokensTotal(cell);
  if (metric === "cost") return cell.costUsd;
  return cell.requests;
}

function typeValue(cell: UsageCell, cat: TypeCat, metric: Metric): number {
  if (metric === "tokens") {
    return cat === "Input"
      ? cell.inputTokens
      : cat === "Cached"
        ? cell.inputCachedTokens
        : cat === "Output"
          ? cell.outputTokens
          : cell.outputImageTokens;
  }
  return cat === "Input"
    ? cell.costInputUsd
    : cat === "Cached"
      ? cell.costInputCachedUsd
      : cat === "Output"
        ? cell.costOutputUsd
        : cell.costOutputImageUsd;
}

function colorMapFor(cats: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  cats.forEach((cat, index) => {
    map[cat] = PALETTE[index % PALETTE.length] ?? "gray.solid";
  });
  return map;
}

/** Sorted category list for a split across a set of cells. */
function categoriesFor(cells: UsageCell[], split: Split): string[] {
  if (split === "type") return [...TYPE_CATS];
  if (split === "total") return ["Total"];
  const keyOf = split === "origin" ? (c: UsageCell) => c.origin : (c: UsageCell) => c.model;
  return [...new Set(cells.map(keyOf))].sort();
}

type SegmentDatum = { name: string; value: number; color: string };
type SeriesRows = { rows: Record<string, number | string>[]; cats: string[]; colorMap: Record<string, string> };
type Dimension = { segments: SegmentDatum[]; series: SeriesRows };

function buildKeyDimension(
  cells: UsageCell[],
  buckets: string[],
  keyOf: (cell: UsageCell) => string,
  metric: Metric,
  colorMap: Record<string, string>,
): Dimension {
  const totalByCat = new Map<string, number>();
  const perBucket = new Map<string, Record<string, number>>();
  for (const bucket of buckets) perBucket.set(bucket, {});
  for (const cell of cells) {
    const cat = keyOf(cell);
    const value = cellMetric(cell, metric);
    totalByCat.set(cat, (totalByCat.get(cat) ?? 0) + value);
    const row = perBucket.get(cell.bucket);
    if (row) row[cat] = (row[cat] ?? 0) + value;
  }
  const cats = [...totalByCat.keys()].sort();
  const segments = cats.map((cat) => ({ name: cat, value: totalByCat.get(cat) ?? 0, color: colorMap[cat] ?? "gray.solid" }));
  const rows = buckets.map((bucket) => {
    const base: Record<string, number | string> = { bucket };
    const row = perBucket.get(bucket) ?? {};
    for (const cat of cats) base[cat] = row[cat] ?? 0;
    return base;
  });
  return { segments, series: { rows, cats, colorMap } };
}

function buildTypeDimension(
  cells: UsageCell[],
  buckets: string[],
  metric: Metric,
  colorMap: Record<string, string>,
): Dimension {
  const perBucket = new Map<string, Record<string, number>>();
  for (const bucket of buckets) perBucket.set(bucket, {});
  const totals: Record<TypeCat, number> = { Input: 0, Cached: 0, Output: 0, Image: 0 };
  for (const cell of cells) {
    const row = perBucket.get(cell.bucket);
    for (const cat of TYPE_CATS) {
      const value = typeValue(cell, cat, metric);
      totals[cat] += value;
      if (row) row[cat] = (row[cat] ?? 0) + value;
    }
  }
  const segments = TYPE_CATS.map((cat) => ({ name: cat, value: totals[cat], color: colorMap[cat] ?? TYPE_COLORS[cat] }));
  const rows = buckets.map((bucket) => {
    const base: Record<string, number | string> = { bucket };
    const row = perBucket.get(bucket) ?? {};
    for (const cat of TYPE_CATS) base[cat] = row[cat] ?? 0;
    return base;
  });
  return { segments, series: { rows, cats: [...TYPE_CATS], colorMap } };
}

// colorMap is keyed by category value (not index) and shared across the month
// and day charts so a given origin/model/type keeps ONE colour even when a day
// only contains a subset of the categories.
function buildDimension(
  cells: UsageCell[],
  buckets: string[],
  split: Split,
  metric: Metric,
  colorMap: Record<string, string>,
): Dimension {
  if (split === "type") return buildTypeDimension(cells, buckets, metric, colorMap);
  const keyOf =
    split === "total" ? () => "Total" : split === "origin" ? (c: UsageCell) => c.origin : (c: UsageCell) => c.model;
  return buildKeyDimension(cells, buckets, keyOf, metric, colorMap);
}

function maxStackHeight(series: SeriesRows): number {
  return series.rows.reduce((max, row) => {
    const sum = series.cats.reduce((acc, cat) => acc + (Number(row[cat]) || 0), 0);
    return Math.max(max, sum);
  }, 0);
}

/**
 * Evenly-spaced "nice" axis ticks from 0 up to at least `maxValue`, using a
 * round step (1/2/2.5/5 × 10ⁿ). Keeps the top gridline on a round number
 * instead of pinning it to the exact data max. `allowFraction` permits 2.5-style
 * steps (fine for cost); token axes stay on integer steps.
 */
function niceTicks(maxValue: number, allowFraction: boolean): number[] {
  const max = Math.max(maxValue, 0);
  if (max <= 0) return [0];
  const rawStep = max / 4;
  const exponent = Math.floor(Math.log10(rawStep));
  const base = Math.pow(10, exponent);
  const fraction = rawStep / base;
  const options = allowFraction ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10];
  const niceFraction = options.find((option) => fraction <= option) ?? 10;
  let step = niceFraction * base;
  if (!allowFraction) step = Math.max(1, Math.round(step));
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= top + step / 2; value += step) {
    ticks.push(Number(value.toPrecision(12)));
  }
  return ticks;
}

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

function UsageLegend({ data, formatValue }: { data: SegmentDatum[]; formatValue: (value: number) => string }) {
  if (data.length === 0) return null;
  return (
    <HStack gap={4} flexWrap="wrap" fontSize="sm">
      {data.map((item) => (
        <HStack key={item.name} gap={1.5}>
          <Box boxSize="2.5" borderRadius="full" bg={item.color} />
          <Text color="fg">{item.name}</Text>
          <Text color="fg.muted" fontWeight="medium">{formatValue(item.value)}</Text>
        </HStack>
      ))}
    </HStack>
  );
}

function CompositionBar({ data, formatValue }: { data: SegmentDatum[]; formatValue: (value: number) => string }) {
  const chart = useChart<SegmentDatum>({ data });
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <Stack gap={3}>
      {total > 0 ? (
        <BarSegment.Root chart={chart} gap="2">
          <BarSegment.Content>
            <BarSegment.Bar />
          </BarSegment.Content>
        </BarSegment.Root>
      ) : (
        <Box h="6" bg="bg.muted" borderRadius="md" />
      )}
      <UsageLegend data={data} formatValue={formatValue} />
    </Stack>
  );
}

// Full-height background band behind the selected category. Rendered as a
// direct BarChart child (recharts 3 renders arbitrary elements) and positioned
// from the live x-axis band scale + plot area — independent of whether any bar
// survives recharts' zero-height filter on that day.
function SelectionOverlay({ activeBucket, buckets }: { activeBucket: string; buckets: string[] }) {
  const plotArea = usePlotArea();
  const scale = useXAxisScale() as
    | (((value: string) => number | undefined) & { bandwidth?: () => number })
    | undefined;
  if (!plotArea || !scale) return null;

  // recharts' scale here returns the LEFT EDGE of each category's slot (no
  // bandwidth), so the rect starts at scale(bucket) and spans one slot width.
  const left = scale(activeBucket);
  if (left == null || Number.isNaN(left)) return null;

  // Slot width = distance to an adjacent bucket; fall back to an even split.
  const evenStep = plotArea.width / Math.max(buckets.length, 1);
  const index = buckets.indexOf(activeBucket);
  const neighbor = buckets[index + 1] ?? buckets[index - 1];
  const neighborX = neighbor != null ? scale(neighbor) : undefined;
  const step =
    neighborX != null && !Number.isNaN(neighborX) && Math.abs(neighborX - left) > 0
      ? Math.abs(neighborX - left)
      : evenStep;

  return <rect x={left} y={plotArea.y} width={step} height={plotArea.height} rx={2} fill={SELECT_BG} />;
}

// Tick that renders its label at the bar's LEFT EDGE instead of its centre, so a
// bar reads as the interval between two boundary labels (e.g. the 00 bar sits
// between "00:00" and "01:00"). payload.offset is half the band width, and the
// tick's x is the band centre, so x - offset lands on the left edge.
function EdgeTick(props: {
  x?: number;
  y?: number;
  fill?: string;
  formatBucket?: (bucket: string) => string;
  payload?: { value: string | number; offset?: number };
}) {
  const { x, y, fill, formatBucket, payload } = props;
  if (x == null || y == null || !payload) return null;
  const label = formatBucket ? formatBucket(String(payload.value)) : String(payload.value);
  return (
    <text x={x - (payload.offset ?? 0)} y={y} dy={12} textAnchor="middle" fill={fill} fontSize={11}>
      {label}
    </text>
  );
}

function DetailedBars({
  series,
  formatValue,
  formatAxis,
  formatBucket,
  formatTooltipLabel,
  domainFloor,
  fractionalTicks,
  onSelectBucket,
  activeBucket,
  edgeTicks,
}: {
  series: SeriesRows;
  formatValue: (value: number) => string;
  formatAxis: (value: number) => string;
  formatBucket: (bucket: string) => string;
  formatTooltipLabel: (bucket: string) => string;
  domainFloor: number;
  fractionalTicks: boolean;
  onSelectBucket?: (bucket: string) => void;
  activeBucket?: string | null;
  edgeTicks?: boolean;
}) {
  // Anchor a zero field so an invisible bar can keep the value axis alive when
  // there are no real series (empty day) — otherwise recharts renders no y-axis.
  const data = useMemo(() => series.rows.map((row) => ({ ...row, __anchor: 0 })), [series.rows]);
  const chart = useChart<Record<string, number | string>>({
    data,
    series: series.cats.map((cat) => ({ name: cat, color: series.colorMap[cat] ?? "gray.solid", stackId: "a" })),
  });

  // Nice, evenly-spaced ticks with an explicit domain so the axis is scientific
  // and still renders for an empty chart.
  const ticks = niceTicks(Math.max(maxStackHeight(series), domainFloor), fractionalTicks);
  const yTop = ticks[ticks.length - 1] ?? domainFloor;
  const hasSeries = chart.series.length > 0;

  return (
    // aspectRatio="auto" defeats Chart.Root's default landscape ratio so the
    // explicit height wins; ResponsiveContainer then guarantees a sized SVG.
    <Chart.Root chart={chart} aspectRatio="auto" h={CHART_HEIGHT}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chart.data}
          onClick={(state: { activeLabel?: string | number } | null) => {
            const label = state?.activeLabel;
            if (label !== undefined && onSelectBucket) onSelectBucket(String(label));
          }}
          style={onSelectBucket ? { cursor: "pointer" } : undefined}
        >
          <CartesianGrid stroke={chart.color(GRID_STROKE)} strokeOpacity={0.45} vertical={false} />
          <XAxis
            dataKey={chart.key("bucket")}
            tickFormatter={edgeTicks ? undefined : formatBucket}
            tick={edgeTicks ? <EdgeTick fill={chart.color("fg.muted")} formatBucket={formatBucket} /> : undefined}
            tickLine={false}
            axisLine={false}
            fontSize={11}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={formatAxis}
            tickLine={false}
            axisLine={false}
            width={64}
            fontSize={11}
            ticks={ticks}
            domain={[0, yTop]}
          />
          {onSelectBucket && activeBucket ? (
            <Customized
              component={
                <SelectionOverlay activeBucket={activeBucket} buckets={series.rows.map((row) => String(row.bucket))} />
              }
            />
          ) : null}
          {hasSeries ? (
            <Tooltip
              cursor={{ fill: HOVER_CURSOR }}
              content={
                <Chart.Tooltip
                  formatter={(value) => formatValue(Number(value))}
                  labelFormatter={(label) => formatTooltipLabel(String(label))}
                />
              }
            />
          ) : (
            // Invisible zero bar so recharts still renders the y-axis.
            <Bar dataKey="__anchor" stackId="a" fill="none" isAnimationActive={false} legendType="none" />
          )}
          {chart.series.map((item) => (
            <Bar
              key={String(item.name)}
              dataKey={chart.key(item.name)}
              fill={chart.color(item.color)}
              stackId={item.stackId}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Chart.Root>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CeMMChatPage() {
  const effectivePersonId = useEffectivePersonId();
  // Cost by model first: what it costs and what drives it is the question
  // people open the page with.
  const [metric, setMetric] = useState<Metric>("cost");
  const [split, setSplit] = useState<Split>("model");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const month = useMemo(() => {
    const today = viennaTodayStr();
    const [y, m] = today.split("-").map(Number);
    const prefix = today.slice(0, 7);
    const from = `${prefix}-01`;
    const lastDay = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
    const to = `${prefix}-${String(lastDay).padStart(2, "0")}`;
    const title = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Vienna",
      month: "long",
      year: "numeric",
    }).format(new Date());
    return { from, to, days: enumerateDays(from, to), title };
  }, []);

  const monthCells = useQuery(api.chatUsage.myUsageCells, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
    fromDay: month.from,
    toDay: month.to,
    bucket: "day",
  }) as UsageCell[] | undefined;

  const dayCells = useQuery(
    api.chatUsage.myUsageCells,
    selectedDay
      ? {
          effectivePersonId: effectivePersonId as Id<"persons"> | null,
          fromDay: selectedDay,
          toDay: selectedDay,
          bucket: "hour" as const,
        }
      : "skip",
  ) as UsageCell[] | undefined;

  const totals = useMemo(() => {
    return (monthCells ?? []).reduce(
      (acc, cell) => ({
        cost: acc.cost + cell.costUsd,
        requests: acc.requests + cell.requests,
        tokens: acc.tokens + tokensTotal(cell),
      }),
      { cost: 0, requests: 0, tokens: 0 },
    );
  }, [monthCells]);

  // One category→colour map for the whole page, derived from the month's
  // categories (the superset), so colours stay stable when a selected day only
  // has a subset of the split values.
  const colorMap = useMemo(() => {
    if (split === "type") return TYPE_COLORS as Record<string, string>;
    return colorMapFor(categoriesFor(monthCells ?? [], split));
  }, [monthCells, split]);

  const monthDimension = useMemo(
    () => buildDimension(monthCells ?? [], month.days, split, metric, colorMap),
    [monthCells, month.days, split, metric, colorMap],
  );

  const dayDimension = useMemo(
    () => buildDimension(dayCells ?? [], HOUR_BUCKETS, split, metric, colorMap),
    [dayCells, split, metric, colorMap],
  );

  const formatValue =
    metric === "tokens"
      ? (v: number) => `${tokensFull.format(v)} tokens`
      : metric === "requests"
        ? (v: number) => `${tokensFull.format(v)} requests`
        : formatCostValue;
  const formatAxis = metric === "cost" ? formatCostAxis : (v: number) => tokensCompact.format(v);
  const domainFloor = metric === "cost" ? 0.01 : metric === "requests" ? 4 : 10;

  // Label of the currently-selected unit, reused in the group label and chart headings.
  const unitLabel = METRIC_OPTIONS.find((option) => option.value === metric)?.label ?? "";

  // Requests have no per-token-type breakdown, so hide the "type" grouping for them.
  const splitOptions = metric === "requests" ? SPLIT_OPTIONS.filter((option) => option.value !== "type") : SPLIT_OPTIONS;

  const isLoading = monthCells === undefined;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="CeMM Chat"
        description="Your CeMM-Chat usage and cost this month."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "CeMM Chat" }]}
      />

      <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {/* KPIs — each its own card; always current month; order cost, tokens, requests */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={5}>
          <StatCard
            label={`Cost · ${month.title}`}
            value={formatCostValue(totals.cost)}
            detail="This month so far"
            icon={LuDollarSign}
            tone="primary"
          />
          <StatCard
            label={`Tokens · ${month.title}`}
            value={tokensFull.format(totals.tokens)}
            detail="Input + cached + output tokens"
            icon={LuCoins}
            tone="secondary"
          />
          <StatCard
            label={`Requests · ${month.title}`}
            value={tokensFull.format(totals.requests)}
            detail="Messages, in chat and through the API"
            icon={LuMessagesSquare}
            tone="info"
          />
        </SimpleGrid>

        {/* Everything else lives in one card */}
        <Card.Root borderWidth="1px" borderColor="border">
          <Card.Body gap={6} p={{ base: 5, lg: 6 }}>
            <Text {...SECTION_LABEL_PROPS}>Details · {month.title}</Text>

            <Stack direction={{ base: "column", md: "row" }} gap={4} justify="space-between">
              <Stack gap={1.5}>
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">Show</Text>
                <SegmentGroup.Root
                  value={metric}
                  onValueChange={(e) => {
                    if (!e.value) return;
                    const next = e.value as Metric;
                    setMetric(next);
                    // "type" grouping is unavailable for requests — fall back to no grouping.
                    if (next === "requests" && split === "type") setSplit("total");
                  }}
                  size="sm"
                >
                  <SegmentGroup.Indicator />
                  {METRIC_OPTIONS.map((option) => (
                    <SegmentGroup.Item key={option.value} value={option.value} cursor="pointer">
                      <SegmentGroup.ItemText>{option.label}</SegmentGroup.ItemText>
                      <SegmentGroup.ItemHiddenInput />
                    </SegmentGroup.Item>
                  ))}
                </SegmentGroup.Root>
              </Stack>

              <Stack gap={1.5} align={{ base: "flex-start", md: "flex-end" }}>
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">Split by</Text>
                <SegmentGroup.Root value={split} onValueChange={(e) => e.value && setSplit(e.value as Split)} size="sm">
                  <SegmentGroup.Indicator />
                  {splitOptions.map((option) => (
                    <SegmentGroup.Item key={option.value} value={option.value} cursor="pointer">
                      <SegmentGroup.ItemText>{option.label}</SegmentGroup.ItemText>
                      <SegmentGroup.ItemHiddenInput />
                    </SegmentGroup.Item>
                  ))}
                </SegmentGroup.Root>
              </Stack>
            </Stack>

            {isLoading ? (
              <Stack gap={6} role="status" aria-busy="true" aria-label="Loading usage">
                <Skeleton height="8" />
                <Skeleton height={CHART_HEIGHT} />
              </Stack>
            ) : (
              <Stack gap={6}>
                {/* Month composition (no heading) */}
                <CompositionBar data={monthDimension.segments} formatValue={formatValue} />

                <Separator />

                {/* Daily — click a day to toggle its hourly breakdown */}
                <Stack gap={3}>
                  {/* How to use the chart, beside the chart rather than in the
                      page's description. */}
                  <HStack justify="space-between" align="baseline" gap={3} flexWrap="wrap">
                    <Heading size="sm">{unitLabel} per day</Heading>
                    <Text fontSize="xs" color="fg.muted">Click a day to see it by hour.</Text>
                  </HStack>
                  <DetailedBars
                    series={monthDimension.series}
                    formatValue={formatValue}
                    formatAxis={formatAxis}
                    formatBucket={(b) => b.slice(8)}
                    formatTooltipLabel={(b) => b}
                    domainFloor={domainFloor}
                    fractionalTicks={metric === "cost"}
                    onSelectBucket={(b) => setSelectedDay((prev) => (prev === b ? null : b))}
                    activeBucket={selectedDay}
                  />
                </Stack>

                <Separator />

                {/* Hourly — always visible */}
                <Stack gap={3}>
                  <Heading size="sm">{unitLabel} per hour{selectedDay ? `: ${selectedDay}` : ""}</Heading>
                  {!selectedDay ? (
                    <Flex h={CHART_HEIGHT} align="center" justify="center">
                      <Text fontSize="sm" color="fg.muted">Select a day above to show its hourly usage.</Text>
                    </Flex>
                  ) : dayCells === undefined ? (
                    <Skeleton height={CHART_HEIGHT} role="status" aria-busy="true" aria-label="Loading the hourly breakdown" />
                  ) : (
                    <DetailedBars
                      series={dayDimension.series}
                      formatValue={formatValue}
                      formatAxis={formatAxis}
                      formatBucket={(b) => `${b}:00`}
                      formatTooltipLabel={(b) => `${b}:00 to ${String(Number(b) + 1).padStart(2, "0")}:00`}
                      domainFloor={domainFloor}
                      fractionalTicks={metric === "cost"}
                      edgeTicks
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Card.Body>
        </Card.Root>
      </Stack>
    </Box>
  );
}
